<script lang="ts" setup>
import AccordionCard from '~/components/cards/AccordionCard.vue'
import Page from '~/enums/page'
import type { BreadcrumbsItem } from '~/types/breadcrumbs'

const breadcrumbsItems: BreadcrumbsItem[] = [{ name: 'Главная', path: Page.Main }]
const title = 'Часто задаваемые вопросы (FAQ)'

useHead({
  title: `${title} — iTAB`,
  meta: [{
    name: 'description',
    content: 'У вас есть вопрос? Здесь вы найдёте ответ.'
  }]
})
</script>

<template>
  <section>
    <div class="container">
      <Breadcrumbs :current="title" :items="breadcrumbsItems" />

      <div class="content-wrapper">
        <h1 class="mb-10">
          {{ title }}
        </h1>

        <AccordionCard class="mb-6">
          <template #trigger>
            Что такое iTAB?
          </template>
          <template #content>
            <p>iTAB&nbsp;— это онлайн-магазин только качественных БАД и&nbsp;продуктов для здорового образа жизни. У&nbsp;нас вы&nbsp;найдёте витамины, добавки, суперфуды, безглютеновые и&nbsp;безлактозные продукты. Товары отбираются ведущими врачами и&nbsp;нутрициологами для обеспечения максимальной эффективности и&nbsp;безопасности. Наша миссия&nbsp;— содействие качественному долголетию соотечественников.</p>
          </template>
        </AccordionCard>

        <AccordionCard class="mb-6">
          <template #trigger>
            Как оформить заказ?
          </template>
          <template #content>
            <p>Заказ оформляется на&nbsp;нашем сайте. Вам необходимо зайти в&nbsp;каталог и&nbsp;положить в&nbsp;корзину желанные товары. Далее система поможет организовать доставку и&nbsp;оплатить заказ.</p>
            <p>
              <NuxtLink :to="Page.Search">
                <span>Ссылка на&nbsp;каталог</span>
              </NuxtLink>.
            </p>
          </template>
        </AccordionCard>

        <AccordionCard class="mb-6">
          <template #trigger>
            Могу&nbsp;ли я&nbsp;оформить заказ по&nbsp;телефону или в&nbsp;IG (@itabpro)?
          </template>
          <template #content>
            <p>Нет, заказ оформляется только на&nbsp;нашем сайте.</p>
          </template>
        </AccordionCard>

        <AccordionCard class="mb-6">
          <template #trigger>
            Могу&nbsp;ли я&nbsp;оплатить заказ наличными (включая оплату курьеру или при самовывозе) или картой при получении?
          </template>
          <template #content>
            <p>Нет, оплата осуществляется только безналично на&nbsp;сайте при оформлении заказа. На&nbsp;указанную почту обязательно придёт чек.</p>
          </template>
        </AccordionCard>

        <AccordionCard class="mb-6">
          <template #trigger>
            Смогу&nbsp;ли я&nbsp;сам(а) найти&nbsp;то, что мне нужно?
          </template>
          <template #content>
            <p>Если вы&nbsp;ищете конкретный продукт (например, витамин Д)&nbsp;— просто впишите его в&nbsp;поисковую строку. Если продукт ещё не&nbsp;известен, но&nbsp;понятен запрос (плохо сплю, быстро устаю и&nbsp;т.&nbsp;д.), с&nbsp;выбором помогут определиться категории. Просто щёлкните на&nbsp;нужную систему организма, и&nbsp;сайт покажет только те&nbsp;продукты, которые эффективны в&nbsp;вашем случае. Также читайте описание продуктов и&nbsp;отзывы.</p>
          </template>
        </AccordionCard>

        <AccordionCard class="mb-6">
          <template #trigger>
            Как получить заказ?
          </template>
          <template #content>
            <p class="mb-0">
              Получить заказ вы&nbsp;можете тремя способами:
            </p>
            <ul class="mb-6">
              <li>1. Оформить курьерскую доставку до&nbsp;двери.</li>
              <li>2. Оформить доставку до&nbsp;ПВЗ (пункта выдачи заказов) наших партнёров.</li>
              <li>3. Забрать заказ самостоятельно из&nbsp;пункта самовывоза по&nbsp;адресу: г. Москва, ул. Голубинская, д. 4А, стр.&nbsp;2&nbsp;(ежедневно с&nbsp;10:00 до&nbsp;18:00).</li>
            </ul>
          </template>
        </AccordionCard>

        <AccordionCard class="mb-6">
          <template #trigger>
            В&nbsp;каком пункте выдачи заказов (ПВЗ) я&nbsp;могу получить свой заказ?
          </template>
          <template #content>
            <p>В&nbsp;любом удобном ПВЗ наших партнёров: вы&nbsp;сможете выбрать его на&nbsp;карте при оформлении заказа.</p>
          </template>
        </AccordionCard>

        <AccordionCard class="mb-6">
          <template #trigger>
            Обрабатываются&nbsp;ли заказы в&nbsp;выходные?
          </template>
          <template #content>
            <p>Да, в&nbsp;выходные заказы обрабатываются и&nbsp;отправляются.</p>
          </template>
        </AccordionCard>

        <AccordionCard class="mb-6">
          <template #trigger>
            Как я&nbsp;могу отследить свой заказ?
          </template>
          <template #content>
            <p>На&nbsp;указанный при оформлении заказа адрес электронной почты придёт письмо с&nbsp;трек-номером заказа. Чтобы отследить заказ, нужно перейти на&nbsp;сайт курьерской службы и&nbsp;ввести трек-номер. По&nbsp;прибытии в&nbsp;пункт выдачи заказов (ПВЗ) на&nbsp;указанный номер телефона придёт уведомление.</p>
          </template>
        </AccordionCard>

        <AccordionCard>
          <template #trigger>
            Какие производители представлены на&nbsp;iTAB?
          </template>
          <template #content>
            <p>На&nbsp;iTAB представлены бренды российских, американских и&nbsp;европейских производителей. Все они имеют декларацию соответствия и/или свидетельство о&nbsp;государственной регистрации.</p>
          </template>
        </AccordionCard>
      </div>
    </div>
  </section>
</template>
